import {
	Component,
	computed,
	EventEmitter,
	inject,
	Input,
	OnChanges,
	OnInit,
	Output,
	signal,
	SimpleChanges,
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	CommentsComponent,
	EmptyStateComponent,
	IToolbar,
	ModalComponent,
	TileComponent,
	ToolbarComponent,
} from '@tecalisdev/infinity';

import { IProcessSentMessage } from '../../models/processes.models';
import {
	buildSentMessagesToolbar,
	filterSentMessages,
	IProcessSentMessageView,
	ISentMessageRowView,
	ISentMessagesFilterState,
	mapSentMessageToRow,
	mergeSentMessages,
} from '../../utils/sent-messages.util';

@Component({
	selector: 'app-modal-sent-messages',
	standalone: true,
	imports: [
		TranslatePipe,
		ModalComponent,
		ToolbarComponent,
		TileComponent,
		EmptyStateComponent,
		CommentsComponent,
	],
	templateUrl: './modal-sent-messages.component.html',
	styleUrl: './modal-sent-messages.component.scss',
})
export class ModalSentMessagesComponent implements OnInit, OnChanges {
	private translate = inject(TranslateService);

	@Input() sms: IProcessSentMessage[] = [];
	@Input() whatsapp: IProcessSentMessage[] = [];
	@Output() closeEvent = new EventEmitter<void>();

	private readonly allMessages = signal<IProcessSentMessageView[]>([]);
	private readonly filters = signal<ISentMessagesFilterState>({ search: '' });

	/**
	 * Toolbar estable: no reinyectar `filters` en `search.value`.
	 * Si no, tec-toolbar re-emite formFilter en bucle y tumba la app.
	 */
	public readonly toolbar = computed<IToolbar>(() => buildSentMessagesToolbar((key) => this.translate.instant(key)));

	public readonly messageRows = computed<ISentMessageRowView[]>(() => {
		const translateFn = (key: string) => this.translate.instant(key);
		return filterSentMessages(this.allMessages(), this.filters(), translateFn).map((message) =>
			mapSentMessageToRow(message, translateFn),
		);
	});

	ngOnInit(): void {
		this.refreshMessages();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['sms'] || changes['whatsapp']) {
			this.refreshMessages();
		}
	}

	public onFilter(raw: Record<string, unknown>): void {
		const searchRaw = raw['search'];
		const search = typeof searchRaw === 'string' ? searchRaw.trim() : '';
		const next: ISentMessagesFilterState = {
			search,
			start_at: normalizeFilterDate(raw['start_at']),
			end_at: normalizeFilterDate(raw['end_at']),
		};
		const current = this.filters();
		if (
			current.search === next.search &&
			sameFilterDate(current.start_at, next.start_at) &&
			sameFilterDate(current.end_at, next.end_at)
		) {
			return;
		}
		this.filters.set(next);
	}

	public closeModal(): void {
		this.closeEvent.emit();
	}

	private refreshMessages(): void {
		this.allMessages.set(mergeSentMessages(this.sms, this.whatsapp));
	}
}

function normalizeFilterDate(value: unknown): string | Date | null {
	if (value == null || value === '') {
		return null;
	}
	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? null : value;
	}
	if (typeof value === 'string') {
		return value;
	}
	return null;
}

function sameFilterDate(a?: string | Date | null, b?: string | Date | null): boolean {
	if (a == null && b == null) {
		return true;
	}
	if (a == null || b == null) {
		return false;
	}
	const aMs = a instanceof Date ? a.getTime() : new Date(a).getTime();
	const bMs = b instanceof Date ? b.getTime() : new Date(b).getTime();
	if (Number.isNaN(aMs) && Number.isNaN(bMs)) {
		return true;
	}
	return aMs === bMs;
}
